<template>

    <Head :title="__('Login')" />

    <div class="flex align-items-center justify-content-center min-h-screen p-3">
        <v-form name="formLogin"
                class="w-full"
                style="max-width:26rem"
                @keyup.enter="login">
            <Card>
                <template #header>
                    <div class="flex justify-content-center pt-4">
                        <Image :src="systemLogo"
                               width="140" />
                    </div>
                </template>

                <template #title>
                    <div class="text-center">{{ __('Login') }}</div>
                </template>

                <template #content>
                    <Message v-if="errorGa2FaLogin"
                             severity="error"
                             :closable="false"
                             class="mb-3">
                        {{ __('Invalid code specified in the second security level') }}
                    </Message>
                    <Message v-if="errorAzure"
                             severity="error"
                             :closable="false"
                             class="mb-3">
                        {{ errorAzure }}
                    </Message>

                    <div class="flex flex-column gap-2 mb-3">
                        <label for="email">{{ __('E-Mail Address') }}</label>
                        <InputText type="text"
                                   id="email"
                                   size="small"
                                   class="w-full"
                                   :invalid="!!form.errors.email"
                                   @input="form.errors.email = null"
                                   v-model="form.email" />
                        <small v-if="form.errors.email"
                               class="p-error">{{ __(form.errors.email) }}</small>
                        <small v-if="form.errors.timesToLock"
                               class="p-error">{{ __(form.errors.timesToLock) }}</small>
                    </div>

                    <div class="flex flex-column gap-2 mb-3">
                        <label for="password">{{ __('Password') }}</label>
                        <Password inputId="password"
                                  size="small"
                                  class="w-full"
                                  inputClass="w-full"
                                  :feedback="false"
                                  toggleMask
                                  :invalid="!!form.errors.password"
                                  @input="form.errors.password = null"
                                  v-model="form.password" />
                        <small v-if="form.errors.password"
                               class="p-error">{{ __(form.errors.password) }}</small>
                    </div>

                    <div class="flex align-items-center mb-2">
                        <Checkbox inputId="remember"
                                  v-model="form.remember"
                                  binary />
                        <label for="remember"
                               class="ml-2">{{ __('Remember Me') }}</label>
                    </div>

                    <div class="flex align-items-center mb-4">
                        <Checkbox inputId="removeSession"
                                  v-model="form.removeSession"
                                  binary />
                        <label for="removeSession"
                               class="ml-2">{{ __('Remove session data') }}</label>
                    </div>

                    <div class="flex flex-wrap gap-2">
                        <Button @click="login"
                                label="Login"
                                icon="pi pi-sign-in"
                                class="flex-1" />

                        <Button v-if="isLoginViaGithub"
                                @click="githubLogin"
                                label="Github"
                                severity="primary"
                                icon="pi pi-github"
                                class="flex-1" />

                        <Button v-if="isLoginViaAzure"
                                @click="azureLogin"
                                label="Azure"
                                severity="warning"
                                icon="pi pi-microsoft"
                                class="flex-1" />
                    </div>

                    <div v-if="!isLoginViaLdap"
                         class="text-center mt-4">
                        <Link :href="route('password-forget')"
                              :aria-hidden="false"
                              v-if="route().has('password-forget')">
                            <i class="pi pi-star mr-2"></i>{{ __('Forgot Your Password?') }}
                        </Link>
                    </div>
                </template>
            </Card>
        </v-form>
    </div>
</template>

<script setup>
import { useForm, usePage } from '@inertiajs/vue3'
import { Head } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3'

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Checkbox from 'primevue/checkbox';
import Card from 'primevue/card';
import Message from 'primevue/message';

import Layout from '@layouts/LayoutNoMenuNoFooter.vue'
import Image from '@helper/Image.vue'
import { __, setStoreValue, getStoreValue, getSessionValue, setSessionValue, getFromRoute } from '@helper/utils.js'
import { systemLogo, startSplash } from '@helper/constants.js'

defineOptions({ layout: Layout })

const props = defineProps({
    errorGa2FaLogin: Boolean,
    errorAzure: String,
    isLoginViaAzure: Boolean,
    isLoginViaGithub: Boolean,
    isLoginViaLdap: Boolean,
})

// const isLoginViaGithub = false
// const isLoginViaAzure = true

// -----------------------------------------------------

const page = usePage();
const form = useForm({ email: '', password: '', remember: false, removeSession: false })

// -----------------------------------------------------

setStoreValue('appCaption', page.props.appCaption)
setStoreValue('systemRevision', page.props.systemRevision)

const isReloaded = getSessionValue("reloaded")
if (!isReloaded) {
    setSessionValue("reloaded", true)
}

// -----------------------------------------------------

const login = () => {
    form.post(route('app-login'))
    setSessionValue("reloaded", false)
}

// -----------------------------------------------------

const azureLogin = () => {
    window.location.href = '/azure/login';
    setSessionValue("reloaded", false)
}

// -----------------------------------------------------

const githubLogin = () => {
    window.location.href = '/github/login';
    setSessionValue("reloaded", false)
}

// -----------------------------------------------------

</script>

<style lang="scss" scoped></style>
